/**
 * Brand Template Configuration
 *
 * Maps brand keys to their default document template asset paths.
 * Templates are static assets stored in public/brands/{brand}/templates/.
 * These serve as the brand-level defaults in the template resolution chain:
 *   User override (IndexedDB) → Admin template → Brand default (static asset) → Programmatic export
 */

export type TemplateFormat = "word" | "pptx" | "xlsx";

export interface BrandTemplate {
  /** Unique ID for this template */
  id: string;
  /** Display name */
  name: string;
  /** Template format */
  format: TemplateFormat;
  /** Path to the template file (relative to public/) */
  path: string;
  /** Short description of the template's purpose */
  description?: string;
  /** Whether this is the default template for the format */
  isDefault?: boolean;
}

export interface BrandTemplateConfig {
  /** All available templates for this brand */
  templates: BrandTemplate[];
}

/**
 * Legacy single-path accessors for backward compatibility.
 * Returns the path of the default template for the given format.
 */
export function getDefaultTemplatePath(
  brandKey: string,
  format: TemplateFormat,
): string | undefined {
  const config = BRAND_TEMPLATES[brandKey];

  if (!config) return undefined;

  const defaultTemplate = config.templates.find((t) => t.format === format && t.isDefault);

  return defaultTemplate?.path;
}

/**
 * Get all templates for a brand and format.
 */
export function getTemplatesForFormat(brandKey: string, format: TemplateFormat): BrandTemplate[] {
  const config = BRAND_TEMPLATES[brandKey];

  if (!config) return [];

  return config.templates.filter((t) => t.format === format);
}

/**
 * Get a specific template by ID.
 */
export function getTemplateById(brandKey: string, templateId: string): BrandTemplate | undefined {
  const config = BRAND_TEMPLATES[brandKey];

  if (!config) return undefined;

  return config.templates.find((t) => t.id === templateId);
}

/**
 * Brand template mappings.
 * Keys should match the brand key used in the brand configuration.
 */
export const BRAND_TEMPLATES: Record<string, BrandTemplateConfig> = {
  ngv: {
    templates: [
      {
        id: "ngv-pptx-default",
        name: "NGV Presentation",
        format: "pptx",
        path: "/brands/ngv/templates/ngv-presentation.pptx",
        description: "Standard NGV branded presentation",
        isDefault: true,
      },
      {
        id: "ngv-word-default",
        name: "NGV Report",
        format: "word",
        path: "/brands/ngv/templates/ngv-report.docx",
        description: "Standard NGV branded report",
        isDefault: true,
      },
      {
        id: "ngv-xlsx-default",
        name: "NGV Data Export",
        format: "xlsx",
        path: "/brands/ngv/templates/ngv-data.xlsx",
        description: "Standard NGV branded spreadsheet with cover page",
        isDefault: true,
      },
    ],
  },
  // TODO(NGET): currently mirrors the NGV templates (same files copied into
  // /public/brands/nget/templates/). Replace with NGET-branded files once design ships.
  // Note: the `ngv` and `nget` keys must coexist — they are intentional duplicates, not
  // dedup candidates. The NGET copies live under /public/brands/nget/templates/ so the
  // brand can ship its own files later without touching the NGV assets.
  nget: {
    templates: [
      {
        id: "nget-pptx-default",
        name: "NGET Presentation",
        format: "pptx",
        path: "/brands/nget/templates/nget-presentation.pptx",
        description: "Standard NGET branded presentation",
        isDefault: true,
      },
      {
        id: "nget-word-default",
        name: "NGET Report",
        format: "word",
        path: "/brands/nget/templates/nget-report.docx",
        description: "Standard NGET branded report",
        isDefault: true,
      },
      {
        id: "nget-xlsx-default",
        name: "NGET Data Export",
        format: "xlsx",
        path: "/brands/nget/templates/nget-data.xlsx",
        description: "Standard NGET branded spreadsheet with cover page",
        isDefault: true,
      },
    ],
  },
  // webuild: no brand default templates — users can upload their own via Settings → Exports
};

/**
 * Resolve the brand key from a Brand object.
 * Handles different brand name formats to find the correct template config.
 */
export function resolveBrandKey(brandName: string): string | undefined {
  const normalised = brandName.toLowerCase().replace(/\s+/g, "");

  // Direct match
  if (BRAND_TEMPLATES[normalised]) return normalised;

  // Known mappings
  const mappings: Record<string, string> = {
    nationalgridventuresai: "ngv",
    nationalgridventures: "ngv",
    ngv: "ngv",
    // TODO(NGET): drop the NGV-name aliases below once react-auth ships an NGET-specific
    // brand name. Until then, NGET inherits NGV's brand.name at runtime, so brand-name
    // lookups still resolve to "ngv"; the `nget`/`nationalgridelectricitytransmission`
    // entries are wired up for when the real NGET brand name lands.
    nget: "nget",
    nationalgridelectricitytransmission: "nget",
    nationalgridelectricitytransmissionai: "nget",
    "webuild-ai": "webuild",
    "webuild-aiazure": "webuild",
    webuildai: "webuild",
    webuild: "webuild",
    investai: "webuild",
  };

  return mappings[normalised];
}
